/**
 * <review-slider> — testimonial slider with a PREV / n of n / NEXT control.
 *
 * Uses the Flickity build already bundled in vendor-v4.js, which loads on every
 * page, so this adds no library weight. Cell widths come from CSS (one card
 * plus a peek below 1025px, 2.5 above it) and `watchCSS` is left off because
 * the slider is wanted at every size.
 *
 * Markup contract:
 *   <review-slider>
 *     <div data-review-track> <article>…</article> … </div>
 *     <button data-review-prev>  <span data-review-counter>  <button data-review-next>
 *   </review-slider>
 */
(function () {
  class ReviewSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-review-track]');
      if (!this.track || typeof Flickity === 'undefined') return;

      this.prevButton = this.querySelector('[data-review-prev]');
      this.nextButton = this.querySelector('[data-review-next]');
      this.counter = this.querySelector('[data-review-counter]');

      this.flickity = new Flickity(this.track, {
        accessibility: true,
        cellAlign: 'left',
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: false,
        // One card per step. Grouping was right while three cards fitted the
        // row exactly; with a half card hanging off the edge there is no "row"
        // to advance, and grouping would jump the peek out of view.
        groupCells: false,
        setGallerySize: true,
      });

      this.render = this.render.bind(this);
      this.equalise = this.equalise.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPrev = () => this.flickity.previous();
      this.onNext = () => this.flickity.next();

      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

      this.flickity.on('select', this.render);
      this.render();

      this.equalise();
      window.addEventListener('resize', this.onResize);
      // Webfonts land after init and change the text height.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.equalise);
    }

    /**
     * Equal card heights.
     *
     * CSS cannot do this: Flickity absolutely positions each cell and sizes the
     * viewport from the tallest one, so `height: 100%` on a cell makes the two
     * depend on each other and both collapse. Measuring instead is
     * deterministic -- clear any previous value first so repeated runs do not
     * ratchet the height upwards.
     *
     * Driven from window resize rather than Flickity's own resize event, which
     * this would otherwise re-trigger in a loop.
     */
    equalise() {
      if (!this.flickity) return;

      // Flickity wraps the cells in .flickity-viewport > .flickity-slider once
      // it initialises, so this.track.children is just the viewport. Ask
      // Flickity for the cells instead of walking the DOM.
      const cells =
        typeof this.flickity.getCellElements === 'function'
          ? this.flickity.getCellElements()
          : Array.from(this.querySelectorAll('.review-slider__card'));

      if (!cells.length) return;
      cells.forEach((cell) => {
        cell.style.minHeight = '';
      });

      const tallest = cells.reduce((max, cell) => Math.max(max, cell.offsetHeight), 0);
      if (!tallest) return;

      cells.forEach((cell) => {
        cell.style.minHeight = `${tallest}px`;
      });

      this.flickity.resize();
      // resize() re-solves the slide targets, and how many of them are
      // reachable changes with the breakpoint -- 2.5 cards per view collapses
      // to one. Without this the counter keeps the old total until the next
      // click.
      this.render();
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(this.equalise, 150);
    }

    disconnectedCallback() {
      if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      window.removeEventListener('resize', this.onResize);
      clearTimeout(this.resizeTimer);
      if (this.flickity) this.flickity.destroy();
    }

    /**
     * Positions the track can actually stop at.
     *
     * `contain: true` clamps the trailing slides onto one scroll position
     * instead of dropping them -- see _containSlides in vendor-v4.js -- so
     * slides.length over-counts. Paging off it would advance the counter and
     * leave NEXT enabled while the track stood still, because the last cards
     * are already on screen inside the peek. Deduping the targets counts what
     * the shopper can reach: one per card wherever a card fills the viewport,
     * fewer once several cards share the end position.
     */
    positions() {
      const targets = this.flickity.slides.map((slide) => Math.round(slide.target));
      return {
        targets,
        unique: targets.filter((target, i) => targets.indexOf(target) === i),
      };
    }

    render() {
      const { targets, unique } = this.positions();
      const page = unique.indexOf(targets[this.flickity.selectedIndex]);
      const total = unique.length;

      if (this.counter) this.counter.textContent = `${page + 1} / ${total}`;

      // Disable rather than hide, so the control keeps its width and the
      // heading row does not shift as you page through.
      if (this.prevButton) this.prevButton.disabled = page === 0;
      if (this.nextButton) this.nextButton.disabled = page >= total - 1;
    }
  }

  if (!customElements.get('review-slider')) {
    customElements.define('review-slider', ReviewSlider);
  }
})();
